import { Range } from 'types';

const MS_PER_HOUR = 60 * 60 * 1000;

// Relative range options in hours, descending. Mirrors the relative options
// offered by the TimerangePicker (Last day → Last hour).
const DEFAULT_RANGE_OPTIONS_IN_HOURS = [24, 12, 9, 6, 3, 1];

const toMillis = (value: number | Date): number => (value instanceof Date ? value.getTime() : value);

/**
 * Determines which relative range option should be preselected by default in the
 * TimerangePicker, based on the duration of the available range (project/asset lifetime).
 *
 * - When no range (or no start) is available, defaults to "Last day" (24h). This covers
 *   greener users with full access to the data.
 * - Otherwise returns the largest option that still fits within the available duration,
 *   floored at "Last hour" (1h).
 */
export const getDefaultRangeInHours = (availableRange?: Range): number => {
  if (!availableRange?.from) {
    return 24;
  }

  const from = toMillis(availableRange.from);
  // Use "elapsed time since start" — cap the upper bound at now so that an ongoing booking
  // (whose tsEnd is in the future) doesn't inflate the duration beyond what has actually elapsed.
  const rawTo = availableRange.to ? toMillis(availableRange.to) : Date.now();
  const to = rawTo > Date.now() ? Date.now() : rawTo;
  const durationInHours = (to - from) / MS_PER_HOUR;

  return DEFAULT_RANGE_OPTIONS_IN_HOURS.find((option) => option <= durationInHours) ?? 1;
};
